/**
 * The components a latency policy can be written against, for the pickers that offer them.
 *
 * <h2>Read from the controller, because the id has to be the controller's</h2>
 *
 * Every component id in the alerting service is a **Ziti** id — `AlertController` documents
 * `componentId` as "the component's Ziti id, as the evidence row carries it", and the names
 * on an alert are resolved against the network's Ziti controller.
 *
 * So these lists come from the controller itself, through `ZitiDataService` — the same call
 * the Edge Routers and Services pages list with. A controller row's `id` **is** the Ziti id,
 * so there is nothing to map and nothing to get wrong.
 *
 * The NetFoundry v2 listings would have been the other way in, and are a trap worth naming:
 * `EdgeRouterV2` and `PlatformService` carry *two* ids — `id`, the NetFoundry UUID the v2
 * API is keyed by, and `zitiId`. **A picker bound to the wrong one writes policies no
 * detector ever matches**, and nothing says so, because an id is just a string to the
 * alerting service: the policy stores, serves back, and is silently never read.
 *
 * <h2>What it does not cover</h2>
 *
 * `LINK` and `DETECTOR` have no listing here, and a link deliberately has none: a fabric
 * link is identified by the two edge routers it joins, so a link policy is picked as two
 * routers rather than as a link. {@link listFor} answers an empty list for those kinds, and
 * a form is expected to fall back to a text box rather than show an empty picker.
 */

import { Inject, Injectable, Optional } from '@angular/core';
import { ComponentType } from '@netfoundry-ui/shared/model';
import { isEmpty } from 'lodash';
import { FilterObj, ZITI_DATA_SERVICE, ZitiDataService } from 'ziti-console-lib';

/**
 * One option in a component picker.
 *
 * `id` is the controller's own id, which is what a policy is keyed by, and `name` is what
 * the reader knows the component as — an option's value and its label.
 */
export interface PickableComponent {
    id: string;
    name: string;
}

/**
 * How many rows to ask the controller for.
 *
 * Well above any real network, and far above the library's own default of 100 — which is
 * the number a picker would silently truncate at. Truncation is the failure nobody can see:
 * the component somebody is looking for is simply absent, with nothing saying the list was
 * cut short.
 */
const PAGE_SIZE = 2000;

/**
 * Characters a name query cannot carry.
 *
 * `ziti-console-lib` builds the query as `name contains "<term>"` and quotes the term without
 * escaping it — see `getUrlFilter`. A `"` closes that string early and a trailing `\` escapes
 * the closing quote, so either one turns a query the reader only meant as text into a 400.
 *
 * Dropped rather than sent. Refusing the whole query would leave a picker that stops answering
 * mid-word with nothing on screen to say why, and no component name in a controller carries
 * either character — so removing them narrows nothing anybody was looking for.
 */
const UNQUOTABLE = /["\\]/g;

/** What each kind is called on the controller. */
const CONTROLLER_TYPES: Partial<Record<ComponentType, string>> = {
    EDGE_ROUTER: 'edge-routers',
    SERVICE: 'services',
    IDENTITY: 'identities',
};

/* How many ids one by-id read names, which keeps the controller's filter inside a URL. */
const IDS_PER_READ = 50;

@Injectable({ providedIn: 'root' })
export class LatencyComponentService {
    /**
     * What has been read, keyed by network and kind.
     *
     * Per network, unlike the alert type registry: routers and services belong to a network
     * where detectors belong to the deployment, so a list learned on one network says
     * nothing about another.
     */
    private cache = new Map<string, PickableComponent[]>();

    /** The requests in flight, so three id boxes on one form make one request each, not three. */
    private inFlight = new Map<string, Promise<PickableComponent[]>>();

    /**
     * `@Optional` because this library is built into apps that do not wire a controller
     * client. Without one there is no list to offer, {@link canList} says so, and a form
     * falls back to typing the id — the same answer it gives for a listing it cannot read.
     * A hard dependency would instead take down every page that touches this library.
     */
    constructor(@Optional() @Inject(ZITI_DATA_SERVICE) private zitiService?: ZitiDataService) {}

    /** Whether a kind is one the controller can be asked for a list of. */
    canList(kind?: ComponentType | null): boolean {
        return !!this.zitiService && !!kind && !!CONTROLLER_TYPES[kind];
    }

    /**
     * The components of one kind in one network, by Ziti id.
     *
     * Read once per network and kind and kept, because a picker is opened and closed
     * repeatedly while a form is filled in. Concurrent callers share the request in flight:
     * a link policy renders two edge router boxes, and they must not make two requests for
     * the same list.
     *
     * **Never rejects.** A caller without permission to list routers, or a controller that
     * cannot be reached, still leaves a form that has to be usable — so this answers an
     * empty list and the form falls back to a text box. Failing the page over a picker would
     * take away the only way to set a threshold.
     */
    listFor(networkId: string, kind: ComponentType): Promise<PickableComponent[]> {
        if (isEmpty(networkId) || !this.canList(kind)) {
            return Promise.resolve([]);
        }
        const key = `${networkId}:${kind}`;
        const cached = this.cache.get(key);
        if (cached) {
            return Promise.resolve(cached);
        }
        const pending = this.inFlight.get(key);
        if (pending) {
            return pending;
        }
        const request = this.read(networkId, kind)
            .then((components) => {
                this.cache.set(key, components);
                return components;
            })
            /* An empty list is a form on text boxes, which is worse than a picker and far
             * better than a page that failed. `cache` is left unset so a later open tries
             * again. */
            .catch(() => [] as PickableComponent[])
            .finally(() => {
                this.inFlight.delete(key);
            });
        this.inFlight.set(key, request);
        return request;
    }

    /**
     * The components of one kind whose name matches what somebody has typed.
     *
     * **Asked of the controller rather than filtered here**, which is what makes the picker
     * honest about a large network: {@link listFor} reads one generous page and a network with
     * more components than that page holds would silently offer a list missing the very thing
     * being searched for. A query goes to the controller, so what comes back is chosen from
     * everything rather than from the first {@link PAGE_SIZE} rows.
     *
     * An empty query is {@link listFor} — the cached full list, which is what the picker shows
     * before anybody types and what its dropdown button opens.
     *
     * **Not cached.** A list per network and kind is a handful of entries; a list per query is
     * unbounded, and would go stale against a network somebody is editing in another tab. The
     * caller debounces instead.
     *
     * Never rejects, for the same reason {@link listFor} does not: a reader who cannot list
     * routers still has to be able to set a threshold.
     */
    search(networkId: string, kind: ComponentType, query: string): Promise<PickableComponent[]> {
        const term = (query || '').replace(UNQUOTABLE, '').trim();
        if (isEmpty(term)) {
            return this.listFor(networkId, kind);
        }
        if (isEmpty(networkId) || !this.canList(kind)) {
            return Promise.resolve([]);
        }
        return this.read(networkId, kind, term).catch(() => [] as PickableComponent[]);
    }

    /**
     * The components with these ids, for a listing whose ids may lie outside the one page {@link listFor} reads.
     * Not cached, read in batches, and never rejects: an id the controller cannot name is left out.
     */
    namesFor(networkId: string, kind: ComponentType, ids: string[]): Promise<PickableComponent[]> {
        const wanted = [...new Set(ids.filter((id) => !isEmpty(id)).map((id) => id.replace(UNQUOTABLE, '')))];
        if (isEmpty(networkId) || !this.canList(kind) || wanted.length === 0) {
            return Promise.resolve([]);
        }
        const batches: string[][] = [];
        for (let at = 0; at < wanted.length; at += IDS_PER_READ) {
            batches.push(wanted.slice(at, at + IDS_PER_READ));
        }
        return Promise.all(batches.map((batch) => this.readIds(kind, batch).catch(() => [] as PickableComponent[]))).then(
            (found) => found.flat()
        );
    }

    /** Drops what has been read, for a network switch or a test. */
    reset(): void {
        this.cache.clear();
        this.inFlight.clear();
    }

    /**
     * Asks the controller for one kind, in one page, optionally narrowed by name.
     *
     * `networkId` is not sent: `ZitiDataService` is already pointed at the current network's
     * controller, which is what makes these the same rows the Edge Routers and Services
     * pages show. It stays in the cache key because switching network switches controller.
     *
     * Sorted by name at the controller rather than here, so paging and ordering agree.
     *
     * **`noSearch` and the filter list have to agree.** `getUrlFilter` in `ziti-console-lib`
     * treats `noSearch: true` as "ignore the filters", so a term with the flag left on reads
     * the whole listing and looks, to a caller, exactly like a query that matched everything.
     */
    private read(networkId: string, kind: ComponentType, term?: string): Promise<PickableComponent[]> {
        const client = this.zitiService as ZitiDataService;
        const searching = !isEmpty(term);
        const paging = { ...client.DEFAULT_PAGING, total: PAGE_SIZE, noSearch: !searching, searchOn: 'name' };
        /* `filterName` and `label` are what a data table draws its filter chips from, and this
         * is not one — but they are not optional on the type, so they carry the same term. Only
         * `columnId` and `value` reach the query: `getUrlFilter` builds `name contains "<term>"`
         * from those two. */
        const filters: FilterObj[] = searching
            ? [{ filterName: 'name', columnId: 'name', value: term, label: term as string }]
            : [];
        return client
            .get(CONTROLLER_TYPES[kind] as string, paging, filters)
            .then((result: any) => this.toOptions(result?.data));
    }

    /** Asks the controller for the rows with these ids, as `(id = "a" or id = "b")`. */
    private readIds(kind: ComponentType, ids: string[]): Promise<PickableComponent[]> {
        const client = this.zitiService as ZitiDataService;
        const paging = { ...client.DEFAULT_PAGING, total: ids.length, noSearch: false };
        const filters: FilterObj[] = [{ filterName: 'id', columnId: 'id', value: ids, label: 'id', type: 'MULTISELECT' }];
        return client.get(CONTROLLER_TYPES[kind] as string, paging, filters).then((result: any) => this.toOptions(result?.data));
    }

    /**
     * The controller's rows as picker options.
     *
     * A row's `id` is the controller's own, which is exactly what a policy is keyed by — so
     * this maps rather than translates. A row with no id is dropped: it could only produce
     * an option that keys nothing.
     *
     * The `name` falls back to the id so a row is never blank in the list.
     */
    private toOptions(rows: any[]): PickableComponent[] {
        return (rows || [])
            .filter((row) => !isEmpty(row?.id))
            .map((row) => ({ id: row.id, name: isEmpty(row.name) ? row.id : row.name }));
    }
}
